import { useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode } from 'react'
import { FileText, Film, Loader2, MessageSquareText, Paperclip, Send, Smile, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useSendSMS, useUploadSMSMedia, type UploadedSMSMedia } from '@/api/sms'
import { useNoteInterne } from '@/api/inbox'
import { useTeamMembers, type TeamMember } from '@/api/agents'
import { useProfile } from '@/api/profile'
import { useReponsesRapides, type ReponseRapide } from '@/api/reponsesRapides'
import { filtrerReponses, GererReponses, ListeReponses } from '@/components/dashboard/ReponsesRapides'
import { SmsLineNotice } from '@/components/dashboard/SmsLineNotice'
import { useSelectedCallerId } from '@/hooks/useSelectedCallerId'
import type { MessageChannel } from '@/types/sms'

interface Props {
  phoneNumber: string
  channel?: MessageChannel
  darkMode?: boolean
  /** Appelé après un envoi réussi (ex. rafraîchir le fil unifié). */
  onSent?: () => void
  /** Contenu affiché en haut du composer (ex. choix du canal). */
  topSlot?: ReactNode
  /** La ligne d'envoi (celle du fil) ; sinon le numéro choisi par l'agent.
   * C'est aussi la ligne à laquelle une note interne se rattache. */
  fromNumber?: string | null
  /** « Texto » (défaut) ou « Note interne » (plan Team) : la note reste dans
   * l'équipe, rien ne part au client. */
  mode?: 'sms' | 'note'
  /** Appelé à chaque frappe (« X écrit… » chez les collègues). */
  onTyping?: (mode: 'sms' | 'note') => void
}

const nomMembre = (m: TeamMember) => `${m.user_first_name || ''} ${m.user_last_name || ''}`.trim() || m.user_email

/** « @jo » juste avant le curseur : le début du « @ » et ce qui est tapé après. */
function mentionEnCours(texte: string, curseur: number): { debut: number; requete: string } | null {
  const m = /(^|\s)@([\p{L}\p{N}._-]*)$/u.exec(texte.slice(0, curseur))
  if (!m) return null
  return { debut: curseur - m[2].length - 1, requete: m[2] }
}

const sansAccents = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

const MAX_MEDIA_SIZE = 5 * 1024 * 1024
const ALLOWED_MEDIA_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'audio/mpeg',
  'audio/ogg',
  'application/pdf',
]

// Une vidéo ou un PDF trop lourds pour le MMS voyagent sous forme de lien
// (au-delà de 600 ko pour une vidéo, de 750 ko pour un PDF) : d'où une limite
// bien plus haute, alignée sur SHARE_LINK_MAX_BYTES côté serveur. Vérifiée ici
// pour refuser avant le transfert plutôt qu'après.
const MAX_LINK_SIZE = 75 * 1024 * 1024
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/3gpp']

/**
 * Composer riche d'un texto : pièces jointes MMS (vidéo par lien au-delà de la
 * limite MMS), emojis, numéro sortant choisi par l'agent, canal SMS ou WhatsApp.
 *
 * Partagé par ConversationView (fil SMS) et le fil unifié de la Boîte de réception.
 */
export function SMSComposer({
  phoneNumber,
  channel = 'sms',
  darkMode = false,
  onSent,
  topSlot,
  fromNumber = null,
  mode = 'sms',
  onTyping,
}: Props) {
  const enNote = mode === 'note'
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const sendMutation = useSendSMS()
  const noteMutation = useNoteInterne(phoneNumber, fromNumber)
  const { data: profile } = useProfile()
  const { data: membres = [] } = useTeamMembers()
  const inputRef = useRef<HTMLInputElement>(null)
  // Mentions choisies dans la liste : id → nom inséré (« @Noah B »).
  const [cites, setCites] = useState<Record<number, string>>({})
  const [mention, setMention] = useState<{ debut: number; requete: string } | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)
  const candidats = useMemo(() => {
    if (!enNote || !mention) return []
    const q = sansAccents(mention.requete)
    return membres
      .filter((m) => m.user_id !== profile?.id)
      .filter((m) => !q || sansAccents(nomMembre(m)).includes(q) || sansAccents(m.user_email).startsWith(q))
      .slice(0, 6)
  }, [enNote, mention, membres, profile?.id])
  const uploadMedia = useUploadSMSMedia()
  const selectedCallerId = useSelectedCallerId()

  const [text, setText] = useState('')

  // Réponses toutes faites (Texto seulement) : ouvertes par l'icône, ou par
  // « / » tapé dans un champ vide ; `requete` est ce qui suit le « / ».
  const [reponses, setReponses] = useState<{ source: 'bouton' | 'slash'; requete: string } | null>(null)
  const [reponseIndex, setReponseIndex] = useState(0)
  const [gererOuvert, setGererOuvert] = useState(false)
  const [gererNouvelle, setGererNouvelle] = useState(false)
  const peutGererReponses = profile?.role === 'MANAGER'
  const listeReponses = useReponsesRapides(!enNote)
  const toutesReponses = listeReponses.data ?? []
  const reponsesVisibles = useMemo(
    () => (reponses ? filtrerReponses(toutesReponses, reponses.requete) : []),
    [reponses, toutesReponses],
  )
  // Volontairement restreint aux pièces jointes : le type interdit au
  // compilateur qu'une vidéo (qui voyage par lien) atterrisse ici et parte
  // en MMS, où l'opérateur la rejetterait sans prévenir.
  const [pendingMedia, setPendingMedia] = useState<
    Extract<UploadedSMSMedia, { kind: 'attachment' }>[]
  >([])
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setPendingMedia([])
    setShowEmojiPicker(false)
    setReponses(null)
  }, [phoneNumber])

  // En passant de Texto à Note (et retour), la liste des mentions se ferme.
  useEffect(() => {
    setMention(null)
    setReponses(null)
    inputRef.current?.focus()
  }, [mode])

  const ouvrirReponses = () => {
    setShowEmojiPicker(false)
    setReponseIndex(0)
    setReponses((r) => (r ? null : { source: 'bouton', requete: '' }))
    inputRef.current?.focus()
  }

  /** Met le texte de la réponse dans le champ, sans rien envoyer. Venu du
   * « / », il remplace ce qui a été tapé ; venu de l'icône, il s'insère au
   * curseur. Le champ tient sur une ligne : les retours à la ligne deviennent
   * des espaces. */
  const choisirReponse = (r: ReponseRapide) => {
    const el = inputRef.current
    const texte = r.text.replace(/\s*\n+\s*/g, ' ')
    let suite = texte
    let pos = texte.length
    if (reponses?.source === 'bouton' && text) {
      const curseur = el?.selectionStart ?? text.length
      const avant = text.slice(0, curseur)
      const insere = (avant && !/\s$/.test(avant) ? ' ' : '') + texte
      suite = avant + insere + text.slice(curseur)
      pos = avant.length + insere.length
    }
    setText(suite)
    setReponses(null)
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(pos, pos)
    })
  }

  const gererReponses = () => {
    setGererNouvelle(toutesReponses.length === 0)
    setReponses(null)
    setGererOuvert(true)
  }

  const choisirMention = (m: TeamMember) => {
    const el = inputRef.current
    if (!mention || !el) return
    const curseur = el.selectionStart ?? text.length
    const insere = `@${nomMembre(m)} `
    const suite = text.slice(0, mention.debut) + insere + text.slice(curseur)
    setText(suite)
    setCites((prev) => ({ ...prev, [m.user_id]: nomMembre(m) }))
    setMention(null)
    requestAnimationFrame(() => {
      const pos = mention.debut + insere.length
      el.focus()
      el.setSelectionRange(pos, pos)
    })
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const valeur = e.target.value
    setText(valeur)
    if (valeur.trim()) onTyping?.(mode)
    if (enNote) {
      setMention(mentionEnCours(valeur, e.target.selectionStart ?? valeur.length))
      setMentionIndex(0)
    } else if (valeur.startsWith('/') && (text === '' || reponses?.source === 'slash')) {
      // « / » au début d'un champ vide : la liste s'ouvre et se filtre.
      setReponses({ source: 'slash', requete: valeur.slice(1) })
      setReponseIndex(0)
    } else if (reponses?.source === 'slash') {
      setReponses(null)
    }
  }

  const publierNote = () => {
    const texte = text.trim()
    if (!texte || noteMutation.isPending) return
    // Seules les mentions encore présentes dans le texte comptent.
    const mentions = Object.entries(cites)
      .filter(([, nom]) => texte.includes(`@${nom}`))
      .map(([id]) => Number(id))
    noteMutation.mutate(
      { text: texte, mentions },
      {
        onSuccess: () => {
          setText('')
          setCites({})
          onSent?.()
        },
        onError: () => toast.error(t('inbox.team.noteError', "La note n'a pas été publiée")),
      },
    )
  }

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    for (const file of Array.from(files)) {
      const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type)
      const isPdf = file.type === 'application/pdf'

      if (!isVideo && !ALLOWED_MEDIA_TYPES.includes(file.type)) {
        toast.error(t('sms.errors.typeNonSupporte', { fichier: file.name }))
        continue
      }
      const limit = isVideo || isPdf ? MAX_LINK_SIZE : MAX_MEDIA_SIZE
      if (file.size > limit) {
        toast.error(t('sms.errors.fichierTropGros', { fichier: file.name, max: Math.round(limit / (1024 * 1024)) }))
        continue
      }
      try {
        const uploaded = await uploadMedia.mutateAsync(file)
        if (uploaded.kind === 'link') {
          // On insère le lien dans le champ de saisie au lieu de l'attacher :
          // l'agent voit exactement ce que le destinataire recevra et peut
          // écrire autour avant d'envoyer.
          setText((prev) => (prev.trim() ? `${prev.trim()} ${uploaded.share_url}` : uploaded.share_url))
          if (isPdf) {
            toast.success('Document envoyé par lien', {
              description:
                'Trop lourd pour le message. Le client recevra un lien vers le document, valable 30 jours.',
              duration: 6000,
            })
          } else {
            toast.success('Vidéo envoyée par lien', {
              description:
                "Trop lourde pour s'afficher dans la conversation du destinataire. Il recevra un lien avec un aperçu, valable 30 jours.",
              duration: 6000,
            })
          }
        } else {
          setPendingMedia((prev) => [...prev, uploaded])
          if (isVideo) {
            toast.success('Vidéo jointe au message', {
              description:
                'Assez légère pour apparaître directement dans la conversation du destinataire.',
              duration: 5000,
            })
          }
        }
      } catch (err) {
        console.error('Erreur upload média:', err)
        toast.error(`${file.name}: erreur d'upload`)
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removePendingMedia = (idx: number) => {
    setPendingMedia((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleEmojiSelect = (emoji: { native: string }) => {
    setText((prev) => prev + emoji.native)
    setShowEmojiPicker(false)
  }

  const handleSend = () => {
    if (enNote) return publierNote()
    if ((!text.trim() && pendingMedia.length === 0) || sendMutation.isPending) return
    sendMutation.mutate(
      {
        to_number: phoneNumber,
        message: text.trim(),
        // Sans from_number, le backend tombe sur get_company_main_number()
        // qui peut retourner un numéro Power Dialer (subaccount) → mismatch
        // Twilio (account ID master vs subaccount). On passe explicitement
        // le caller ID sélectionné par l'agent dans le sélecteur de ligne.
        from_number: fromNumber || selectedCallerId || undefined,
        channel,
        // Médias non supportés sur WhatsApp en phase 1 (texte uniquement).
        media_s3_keys:
          channel === 'sms' && pendingMedia.length
            ? pendingMedia.map((m) => m.s3_key)
            : undefined,
      },
      {
        onSuccess: () => {
          setText('')
          setPendingMedia([])
          onSent?.()
        },
      },
    )
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Liste des réponses ouverte : flèches, Entrée pour choisir, Échap pour fermer.
    // Entrée n'envoie jamais tant qu'elle est ouverte.
    if (reponses) {
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && reponsesVisibles.length > 0) {
        e.preventDefault()
        const pas = e.key === 'ArrowDown' ? 1 : -1
        setReponseIndex((i) => (i + pas + reponsesVisibles.length) % reponsesVisibles.length)
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        if (reponsesVisibles.length > 0) {
          choisirReponse(reponsesVisibles[Math.min(reponseIndex, reponsesVisibles.length - 1)])
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setReponses(null)
        return
      }
    }
    // Liste des mentions ouverte : flèches pour choisir, Entrée ou Tab pour insérer.
    if (candidats.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const pas = e.key === 'ArrowDown' ? 1 : -1
        setMentionIndex((i) => (i + pas + candidats.length) % candidats.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        choisirMention(candidats[Math.min(mentionIndex, candidats.length - 1)])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMention(null)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div
      className={cn(
        'flex-shrink-0 border-t',
        darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
      )}
    >
      {topSlot}
      {/* Pending media preview */}
      {pendingMedia.length > 0 && (
        <div className="px-4 pt-3 flex gap-2 flex-wrap">
          {pendingMedia.map((media, idx) => (
            <div key={idx} className="relative group/media">
              {media.content_type.startsWith('image/') ? (
                <img
                  src={media.url}
                  alt={media.filename}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div
                  className={cn(
                    'w-16 h-16 rounded-lg flex flex-col items-center justify-center text-[10px]',
                    darkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500',
                  )}
                >
                  {media.content_type.startsWith('video/') ? (
                    <Film className="w-5 h-5 mb-0.5" />
                  ) : (
                    <FileText className="w-5 h-5 mb-0.5" />
                  )}
                  <span className="truncate max-w-[56px]">
                    {media.filename.split('.').pop()}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={() => removePendingMedia(idx)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/media:opacity-100 transition-opacity hover:bg-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {channel === 'sms' && !enNote && (
        <SmsLineNotice selectedCallerId={selectedCallerId} toNumber={phoneNumber} darkMode={darkMode} />
      )}
      <div className="px-4 py-3 relative">
        {/* Membres à citer (« @ » dans une note interne) */}
        {candidats.length > 0 && (
          <div
            role="listbox"
            className={cn(
              'absolute bottom-full left-4 mb-1 z-50 w-64 rounded-lg border py-1 shadow-lg',
              darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200',
            )}
          >
            {candidats.map((m, i) => (
              <button
                key={m.user_id}
                type="button"
                role="option"
                aria-selected={i === mentionIndex}
                // mousedown : garder le focus dans le champ.
                onMouseDown={(e) => {
                  e.preventDefault()
                  choisirMention(m)
                }}
                onMouseEnter={() => setMentionIndex(i)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-left text-[13px]',
                  i === mentionIndex
                    ? darkMode
                      ? 'bg-slate-800 text-white'
                      : 'bg-slate-100 text-slate-900'
                    : darkMode
                      ? 'text-slate-300'
                      : 'text-slate-700',
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                    darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700',
                  )}
                >
                  {(m.user_first_name?.[0] || m.user_email[0] || '?').toUpperCase()}
                </span>
                <span className="truncate">{nomMembre(m)}</span>
              </button>
            ))}
          </div>
        )}

        {/* Réponses toutes faites (icône ou « / ») */}
        {reponses && !enNote && (
          <ListeReponses
            reponses={reponsesVisibles}
            total={toutesReponses.length}
            chargement={listeReponses.isLoading}
            index={reponseIndex}
            onIndex={setReponseIndex}
            onChoisir={choisirReponse}
            peutGerer={peutGererReponses}
            onGerer={gererReponses}
            darkMode={darkMode}
          />
        )}
        {peutGererReponses && (
          <GererReponses
            open={gererOuvert}
            onOpenChange={setGererOuvert}
            darkMode={darkMode}
            nouvelleDemandee={gererNouvelle}
          />
        )}

        {/* Emoji picker */}
        {showEmojiPicker && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowEmojiPicker(false)}
            />
            <div className="absolute bottom-full left-4 mb-2 z-50">
              <Picker
                data={data}
                onEmojiSelect={handleEmojiSelect}
                theme={darkMode ? 'dark' : 'light'}
                locale={locale}
                previewPosition="none"
                skinTonePosition="search"
                set="native"
                maxFrequentRows={2}
                perLine={8}
              />
            </div>
          </>
        )}

        <div
          className={cn(
            'flex items-center rounded-xl transition-colors',
            enNote
              ? darkMode
                ? 'bg-slate-900 border border-dashed border-slate-700'
                : 'bg-white border border-dashed border-slate-300'
              : darkMode
                ? 'bg-slate-900/80'
                : 'bg-gray-50',
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,audio/mpeg,audio/ogg,application/pdf,video/mp4,video/quicktime,video/webm,video/3gpp"
            onChange={handleFileSelect}
            className="hidden"
          />
          {!enNote && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMedia.isPending || sendMutation.isPending}
            className={cn(
              'flex-shrink-0 ml-2 w-8 h-8 rounded-lg flex items-center justify-center transition-all',
              uploadMedia.isPending
                ? 'animate-pulse text-indigo-400'
                : darkMode
                  ? 'text-slate-500 hover:text-slate-300'
                  : 'text-gray-400 hover:text-gray-600',
            )}
            title={t('sms.attachFile', 'Joindre un fichier')}
          >
            <Paperclip className="w-4 h-4" />
          </button>
          )}
          <button
            type="button"
            onClick={() => {
              setReponses(null)
              setShowEmojiPicker((p) => !p)
            }}
            className={cn(
              'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all',
              enNote && 'ml-2',
              showEmojiPicker
                ? 'text-indigo-400'
                : darkMode
                  ? 'text-slate-500 hover:text-slate-300'
                  : 'text-gray-400 hover:text-gray-600',
            )}
            title="Emojis"
          >
            <Smile className="w-4 h-4" />
          </button>
          {!enNote && (
            <button
              type="button"
              // mousedown : garder le focus dans le champ (flèches, Entrée).
              onMouseDown={(e) => e.preventDefault()}
              onClick={ouvrirReponses}
              className={cn(
                'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all',
                reponses
                  ? 'text-indigo-400'
                  : darkMode
                    ? 'text-slate-500 hover:text-slate-300'
                    : 'text-gray-400 hover:text-gray-600',
              )}
              title={t('reponsesRapides.titre', 'Réponses enregistrées')}
              aria-label={t('reponsesRapides.titre', 'Réponses enregistrées')}
            >
              <MessageSquareText className="w-4 h-4" />
            </button>
          )}
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setMention(null)
              setReponses(null)
            }}
            placeholder={
              enNote
                ? t('inbox.team.notePlaceholder', 'Note pour l\'équipe. @ pour citer un collègue')
                : pendingMedia.length > 0
                  ? t('sms.addMessageOptional', 'Ajouter un message (optionnel)...')
                  : t('sms.typeMessage', 'Tapez un message…')
            }
            disabled={sendMutation.isPending || noteMutation.isPending}
            className={cn(
              'flex-1 bg-transparent border-0 outline-none px-3 py-2.5 text-sm',
              darkMode
                ? 'text-white placeholder:text-slate-500'
                : 'text-slate-900 placeholder:text-slate-400',
            )}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={
              sendMutation.isPending ||
              noteMutation.isPending ||
              (!text.trim() && (enNote || pendingMedia.length === 0))
            }
            title={enNote ? t('inbox.team.publishNote', 'Publier la note') : undefined}
            className={cn(
              'flex-shrink-0 mr-2 w-8 h-8 rounded-lg flex items-center justify-center transition-all',
              enNote && text.trim()
                ? darkMode ? 'bg-slate-200 text-slate-900 hover:bg-white' : 'bg-slate-800 text-white hover:bg-slate-900'
                : text.trim() || pendingMedia.length > 0
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm shadow-indigo-600/20'
                : darkMode
                  ? 'text-slate-600'
                  : 'text-gray-300',
              'disabled:cursor-not-allowed',
            )}
          >
            {sendMutation.isPending || noteMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
